import React, { useState, useEffect, useCallback } from 'react';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';
import { useWebSocketMessage } from '../contexts/WebSocketContext';
import { vpnApi, BandwidthTimeframe, BandwidthStep } from '../api/vpn';
import { BandwidthUpdateMessage } from '../services/websocket';

/**
 * BandwidthChart Component
 *
 * Real-time bandwidth visualization using WebSocket updates and VictoriaMetrics historical data.
 *
 * Architecture (from Issue #63):
 * 1. Clients poll WireGuard stats → send to connection nodes
 * 2. Connection nodes aggregate metrics (30 seconds)
 * 3. Backend writes to VictoriaMetrics (time-series DB)
 * 4. Backend publishes to Redis → WebSocket to frontend
 * 5. Frontend subscribes to bandwidth_update messages
 * 6. Historical data queried from VictoriaMetrics API
 *
 * Data Storage:
 * - **VictoriaMetrics:** All bandwidth metrics and historical data (time-series)
 * - **PostgreSQL:** Only bandwidth limits/quotas and alerts (relational data)
 *
 * Update frequency: Every 30 seconds (matches connection node batching)
 */

interface BandwidthData {
  timestamp: number;
  upload: number;    // bytes/s
  download: number;  // bytes/s
}

interface BandwidthChartProps {
  sessionId: string;
  bytesUploaded: number;
  bytesDownloaded: number;
  className?: string;
}

const BandwidthChart: React.FC<BandwidthChartProps> = ({
  sessionId,
  bytesUploaded,
  bytesDownloaded,
  className = ''
}) => {
  const [history, setHistory] = useState<BandwidthData[]>([]);
  const [currentUploadSpeed, setCurrentUploadSpeed] = useState(0);
  const [currentDownloadSpeed, setCurrentDownloadSpeed] = useState(0);
  const [timeframe, setTimeframe] = useState<BandwidthTimeframe>('1h');
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const MAX_DATA_POINTS = 60;
  const CHART_WIDTH = 400;
  const CHART_HEIGHT = 120;

  // Subscribe to real-time bandwidth updates via WebSocket (Issue #492)
  useWebSocketMessage('bandwidth_update', useCallback((message) => {
    const update = message as BandwidthUpdateMessage;

    // Only process updates for this session
    if (update.session_id !== sessionId) {
      return;
    }

    // Update current speeds from WebSocket
    setCurrentUploadSpeed(update.bytes_per_second_sent);
    setCurrentDownloadSpeed(update.bytes_per_second_received);

    // Add to history
    const newData: BandwidthData = {
      timestamp: new Date(update.timestamp).getTime(),
      upload: update.bytes_per_second_sent,
      download: update.bytes_per_second_received
    };

    setHistory(prev => {
      const updated = [...prev, newData];
      // Keep only last MAX_DATA_POINTS
      return updated.slice(-MAX_DATA_POINTS);
    });
  }, [sessionId]), [sessionId]);

  // Fetch historical bandwidth data from VictoriaMetrics (Issue #492)
  const fetchHistory = useCallback(async () => {
    if (!sessionId) return;

    setIsLoadingHistory(true);
    setHistoryError(null);

    try {
      // Use appropriate step based on timeframe to manage data point count
      const step: BandwidthStep = timeframe === '1h' ? '5m' : timeframe === '24h' ? '15m' : '1h';
      const response = await vpnApi.getBandwidthHistory(sessionId, timeframe, step);

      // Calculate rate from deltas between consecutive cumulative points
      const historyData: BandwidthData[] = response.data.data_points.reduce((acc: BandwidthData[], point, i, arr) => {
        if (i === 0) return acc; // Skip first point (no previous to compare)

        const prevPoint = arr[i - 1];
        const timestamp = new Date(point.timestamp).getTime();
        const prevTimestamp = new Date(prevPoint.timestamp).getTime();
        const intervalSeconds = (timestamp - prevTimestamp) / 1000;

        if (intervalSeconds > 0) {
          // Backend now provides separate bytes_sent and bytes_received (Issue #504)
          const bytesSentDelta = point.bytes_sent - prevPoint.bytes_sent;
          const bytesReceivedDelta = point.bytes_received - prevPoint.bytes_received;

          const uploadRate = Math.max(0, bytesSentDelta / intervalSeconds);
          const downloadRate = Math.max(0, bytesReceivedDelta / intervalSeconds);

          acc.push({
            timestamp,
            upload: uploadRate,
            download: downloadRate,
          });
        }
        return acc;
      }, []);

      setHistory(historyData);
    } catch (error) {
      // Don't show error for 404 (no data yet) - this is expected for new sessions
      if ((error as { response?: { status?: number } })?.response?.status !== 404) {
        setHistoryError('Failed to load bandwidth history');
        console.error('Failed to fetch bandwidth history:', error);
      }
    } finally {
      setIsLoadingHistory(false);
    }
  }, [sessionId, timeframe]);

  // Load historical data on mount and timeframe change
  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const formatSpeed = (bytesPerSec: number): string => {
    if (bytesPerSec >= 1024 * 1024) {
      return `${(bytesPerSec / (1024 * 1024)).toFixed(1)} MB/s`;
    }
    if (bytesPerSec >= 1024) {
      return `${(bytesPerSec / 1024).toFixed(1)} KB/s`;
    }
    return `${bytesPerSec.toFixed(0)} B/s`;
  };

  const formatBytes = (bytes: number): string => {
    if (bytes >= 1024 * 1024 * 1024) {
      return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    }
    if (bytes >= 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }
    if (bytes >= 1024) {
      return `${(bytes / 1024).toFixed(2)} KB`;
    }
    return `${bytes} B`;
  };

  // Calculate chart scales
  const maxSpeed = Math.max(
    ...history.map(d => Math.max(d.upload, d.download)),
    1024 // Minimum scale of 1 KB/s
  );

  const generatePath = (data: BandwidthData[], type: 'upload' | 'download'): string => {
    if (data.length === 0) return '';

    const points = data.map((d, i) => {
      const x = (i / (Math.max(data.length - 1, 1))) * CHART_WIDTH;
      const value = type === 'upload' ? d.upload : d.download;
      const y = CHART_HEIGHT - (value / maxSpeed) * CHART_HEIGHT;
      return `${x},${Math.max(0, Math.min(CHART_HEIGHT, y))}`;
    });

    return `M ${points.join(' L ')}`;
  };

  const uploadPath = generatePath(history, 'upload');
  const downloadPath = generatePath(history, 'download');

  return (
    <div className={`bg-white rounded-lg shadow p-4 ${className}`}>
      {/* Current Speed Display */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-blue-50 rounded-lg p-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium text-primary-800 flex items-center">
              <ArrowDownIcon className="h-4 w-4 mr-1" />
              Download
            </span>
          </div>
          <div className="text-2xl font-bold text-primary-900">
            {formatSpeed(currentDownloadSpeed)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Total: {formatBytes(bytesDownloaded)}
          </div>
        </div>

        <div className="bg-green-50 rounded-lg p-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium text-green-800 flex items-center">
              <ArrowUpIcon className="h-4 w-4 mr-1" />
              Upload
            </span>
          </div>
          <div className="text-2xl font-bold text-green-900">
            {formatSpeed(currentUploadSpeed)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Total: {formatBytes(bytesUploaded)}
          </div>
        </div>
      </div>

      {/* Timeframe Selector */}
      <div className="flex justify-between items-center mb-2">
        <span className="text-xs text-gray-500">Bandwidth History</span>
        <select
          value={timeframe}
          onChange={(e) => setTimeframe(e.target.value as BandwidthTimeframe)}
          className="text-xs border border-gray-300 rounded px-2 py-1"
        >
          <option value="1h">Last Hour</option>
          <option value="24h">Last 24 Hours</option>
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
        </select>
      </div>

      {/* Chart */}
      <div className="relative">
        {isLoadingHistory && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          </div>
        )}

        {historyError && (
          <div className="text-xs text-red-500 mb-2">{historyError}</div>
        )}

        <div className="text-xs text-gray-500 mb-2 flex justify-between">
          <span>{history.length > 0 ? `${history.length} data points` : 'Waiting for data...'}</span>
          <span>{formatSpeed(maxSpeed)} max</span>
        </div>

        <svg
          width="100%"
          height={CHART_HEIGHT}
          viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
          className="border border-gray-200 rounded"
        >
          {/* Grid lines */}
          <line x1="0" y1={CHART_HEIGHT / 2} x2={CHART_WIDTH} y2={CHART_HEIGHT / 2} stroke="#e5e7eb" strokeWidth="1" />
          <line x1="0" y1="0" x2={CHART_WIDTH} y2="0" stroke="#e5e7eb" strokeWidth="1" />
          <line x1="0" y1={CHART_HEIGHT} x2={CHART_WIDTH} y2={CHART_HEIGHT} stroke="#e5e7eb" strokeWidth="1" />

          {/* Download line (blue) */}
          {downloadPath && (
            <path
              d={downloadPath}
              fill="none"
              stroke="#3b82f6"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}

          {/* Upload line (green) */}
          {uploadPath && (
            <path
              d={uploadPath}
              fill="none"
              stroke="#10b981"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}

          {/* No data placeholder */}
          {history.length === 0 && !isLoadingHistory && (
            <text
              x={CHART_WIDTH / 2}
              y={CHART_HEIGHT / 2}
              textAnchor="middle"
              className="fill-gray-400 text-xs"
            >
              No bandwidth data yet
            </text>
          )}
        </svg>

        {/* Legend */}
        <div className="flex justify-center gap-4 mt-2 text-xs">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-primary-500 rounded-full mr-1"></div>
            <span className="text-gray-600">Download</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-green-500 rounded-full mr-1"></div>
            <span className="text-gray-600">Upload</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BandwidthChart;
